import { css } from '@emotion/react';
import React, { useEffect, useState, useContext } from 'react';
import { useFormContext } from 'react-hook-form';

// redux
import { useSelector } from 'react-redux';
import { CollectionCourse } from 'redux/schemas/models/collections';
import { useAppDispatch } from 'redux/store';
import { fetchCollection } from 'redux/actions/collections';
import { getCourse } from 'redux/selectors/course';

// components
import LoadingPlaceholder, { LoaderType } from 'shared/components/loading-placeholder';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import { gray6 } from 'styles/global_defaults/colors';
import NvRadioButton from 'shared/components/inputs/nv-radio-button';
import CollectionRowFolder from './collection-row-folder';
import { CollectionListModalContext, CollectionListModalType } from './collection-list-modal-body';

export const useOnClickExpand = () => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);

    if (!isExpanded) {
      setIsLoading(true);
    }
  };

  return {
    isLoading,
    isExpanded,
    setIsLoading,
    toggleExpanded,
    setIsExpanded,
  };
};

export type Props = {
  catalogId: string
};

const styles = css`
  & {
    border-bottom: solid 1px ${gray6}
  }
`;

const CollectionRowSelectionComponent = (props) => {
  const {
    collectionListModalType,
    isExpanded,
    toggleExpanded,
    collection,
    setIsExpanded,
    setIsLoading,
    foldersCount,
  } = props;

  const { getValues, setValue, trigger } = useFormContext() || {};

  const handleSelectCollection = (e) => {
    const selectedCatalogId = e.currentTarget.value;
    if (foldersCount > 0) {
      setIsExpanded(true);
      setIsLoading(true);
    }
    if (selectedCatalogId !== getValues('destinationCatalogId')) {
      setValue('destinationFolderId', null);
      setValue('destinationCatalogId', selectedCatalogId);
      setValue('destinationCollectionId', collection.id);
      setValue('destinationCollectionName', collection.name);
      trigger();
    }
  };

  const handleBulkSelect = (e) => {
    const selectedCatalogId = e.currentTarget.value;

    if (selectedCatalogId !== getValues('destinationCatalogId')) {
      setValue('destinationCatalogId', selectedCatalogId);
      setValue('destinationCollectionId', collection.id);
      setValue('destinationCollectionName', collection.title);
      trigger();
    }
  };

  switch (collectionListModalType) {
    case CollectionListModalType.LESSON_LINK:
      return (
        <ClickableContainer
          className={`d-flex flex-column ${isExpanded ? 'pb-2' : ''}`}
          onClick={toggleExpanded}
        >
          <div className='d-flex align-items-center'>
            <NvIcon
              icon={`arrow-${isExpanded ? 'down' : 'right'}`}
              size='xss-smallest'
              className='text-gray-1 pr-4'
            />
            <div className='text-large-body font-weight-bolder ellipsis'>
              {collection.title}
            </div>
          </div>
          <div className='ml-5 text-body ellipsis'>
            <span className='text-gray-2 font-weight-normal'>
              {collection.description}
            </span>
          </div>
        </ClickableContainer>
      );
    case CollectionListModalType.FOLDER_TRANSFER:
    case CollectionListModalType.LESSON_TRANSFER:
      return (
        <React.Fragment>
          <div className='radio-container'>
            <NvRadioButton
              withForm
              label={collection.title}
              name='destinationCatalogId'
              value={collection.catalogId}
              className='d-flex'
              labelClassName='d-flex text-wrap mw-100 pl-6 text-large-body font-weight-bolder ellipsis align-items-center'
              onClick={handleSelectCollection}
            />
          </div>
          <div className='pl-6'>
            { collection.description }
          </div>
        </React.Fragment>
      );
    case CollectionListModalType.BULK_LINK:
      return (
        <div>
          <NvRadioButton
            withForm
            label={collection.title}
            name='destinationCatalogId'
            value={collection.catalogId}
            className='d-flex'
            labelClassName='d-flex text-wrap mw-100 pl-6 text-large-body font-weight-bolder ellipsis align-items-center'
            onClick={handleBulkSelect}
          />
          <div className='pl-6 text-body text-gray-2'>
            {collection.description}
          </div>
        </div>
      );
    default:
      return null;
  }
};

const CollectionRow = (props: Props) => {
  const { catalogId } = props;
  const collection: CollectionCourse = useSelector((state) => getCourse(state, catalogId));
  const { folders, foldersCount } = collection;

  const dispatch = useAppDispatch();
  const { isLoading, isExpanded, setIsLoading, toggleExpanded, setIsExpanded } = useOnClickExpand();
  const { collectionListModalType } = useContext(CollectionListModalContext);

  useEffect(() => {
    if (isExpanded
        && collectionListModalType !== CollectionListModalType.FOLDER_TRANSFER
        && foldersCount > 0
    ) {
      dispatch(fetchCollection(String(collection.id))).then(() => {
        setIsLoading(false);
      });
    }
  }, [
    collection.id,
    collectionListModalType,
    dispatch,
    isExpanded,
    setIsLoading,
    foldersCount,
  ]);

  return (
    <div
      className='d-flex flex-column pr-2 py-4'
      css={styles}
      key={catalogId}
    >
      <CollectionRowSelectionComponent
        collectionListModalType={collectionListModalType}
        isExpanded={isExpanded}
        setIsLoading={setIsLoading}
        toggleExpanded={toggleExpanded}
        collection={collection}
        setIsExpanded={setIsExpanded}
        foldersCount={foldersCount}
      />
      {isExpanded && (collectionListModalType !== CollectionListModalType.FOLDER_TRANSFER) && (
        (isLoading && (!folders || folders === null)) ? (
          <LoadingPlaceholder loaderType={LoaderType.HALF} className='p-4' />
        ) : (
          folders?.length > 0 && folders.map((folderId) => (
            <CollectionRowFolder
              folderId={folderId}
              collectionName={collection.title}
              catalogId={catalogId}
              key={folderId}
              collectionId={collection.id}
            />
          ))))}
    </div>
  );
};

export default CollectionRow;
